import { FC, useState } from 'react';
import useGetEmployeeLeaveRequests from './hooks/useGetEmployeeListValues';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { useGetColumns } from './hooks/useGetColumns';
import { Box } from '@mui/material';
import { t } from 'i18next';
import CreateButton from 'components/shared/CreateButton/CreateButton';
import useRemoveLeaveRequest from './hooks/useRemoveLeaveRequest';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import useAddLeaveRequest from './hooks/useAddLeaveRequest';
import { FormProvider, useForm } from 'react-hook-form';
import LeaveRequestForm from './components/LeaveRequestForm';
import { leaveRequestSchema } from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { NewEmployeeLeaveRequest } from 'RestAPIs/employees/leave-requests';
import dayjs from 'dayjs';
import {
  LeaveRequestFormValues,
  LeaveRequestsProps
} from './LeaveRequests.types';

const LeaveRequests: FC<LeaveRequestsProps> = ({ empId: employeeId }) => {
  const columns = useGetColumns();
  const methods = useForm<LeaveRequestFormValues>({
    resolver: yupResolver(leaveRequestSchema),
    defaultValues: {
      startDate: null,
      endDate: null,
      leaveType: '',
      contractKind: '',
      leaveRequestNote: ''
    }
  });

  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState<
    null | number
  >(null);
  const { data: rows, isFetching } = useGetEmployeeLeaveRequests(employeeId);
  const removeLeaveRequest = useRemoveLeaveRequest();
  const hideActions = rows
    ? !rows.some((row) =>
        ['pending', 'seen'].includes(row?.status ?? 'UNKNOWN')
      )
    : true;

  const addLeaveRequestMutation = useAddLeaveRequest();

  const handleOpenRemoveDialog = (leaveRequestId: number) => {
    setSelectedLeaveRequestId(leaveRequestId);
    setIsRemoveDialogOpen(true);
  };

  const handleRemove = (isConfirmed: boolean) => {
    setIsRemoveDialogOpen(false);
    if (isConfirmed) {
      if (selectedLeaveRequestId !== null) {
        removeLeaveRequest.mutate(selectedLeaveRequestId);
        setSelectedLeaveRequestId(null);
      }
    } else {
      setSelectedLeaveRequestId(null);
    }
  };

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
  };

  const handleSave = (isConfirmed: boolean) => {
    isConfirmed
      ? methods.handleSubmit(() => {
          const {
            leaveType,
            contractKind,
            leaveRequestNote,
            startDate,
            endDate,
            isSingleDay,
            startTime,
            endTime
          } = methods.getValues();

          const validStartDate =
            isSingleDay && startTime && dayjs(startTime).isValid()
              ? startTime
              : startDate;
          const validEndDate =
            isSingleDay && endTime && dayjs(endTime).isValid()
              ? endTime
              : endDate;

          if (!validStartDate || !validEndDate) return;

          const newLeaveRequest: NewEmployeeLeaveRequest = {
            dutyTypeId: Number(leaveType),
            empNote: leaveRequestNote,
            valid: {
              from: validStartDate,
              to: validEndDate
            },
            waId: Number(contractKind)
          };

          addLeaveRequestMutation.mutate(newLeaveRequest, {
            onSuccess: () => {
              setIsAddDialogOpen(false);
              methods.reset();
            }
          });
        })()
      : setIsAddDialogOpen(false);
  };

  return (
    <>
      <Box p={3.5}>
        <Box pt={1} pb={4}>
          <CreateButton
            label={t('EmployeeLeaveRequests.new')}
            onClick={() => handleOpenAddDialog()}
          />
        </Box>
        <TableCRUDLayout
          getRowId={(row) => row.leaveRequestId}
          rows={rows || []}
          columns={columns}
          loading={isFetching}
          dynamicHeight={true}
          pageSize={25}
          editable={false}
          disableActions={hideActions}
          FormComponent={() => null}
          hideFooter={rows && rows?.length < 25}
          initialState={{
            sorting: {
              sortModel: [{ field: 'valid.from', sort: 'desc' }]
            }
          }}
          customActions={(_id, row) => [
            ...(row?.status === 'pending' || row?.status === 'seen'
              ? [
                  {
                    tooltipTitle: t('EmployeeLeaveRequests.remove'),
                    icon: 'trash-can',
                    onClick: () =>
                      handleOpenRemoveDialog(row?.leaveRequestId as number)
                  }
                ]
              : [])
          ]}
        />
      </Box>

      {isAddDialogOpen && (
        <ConfirmationDialog
          title={t('EmployeeLeaveRequests.request')}
          type="info"
          handleAction={handleSave}
          cancelBtnText={t('cancel')}
          confirmBtnText={t('EmployeeLeaveRequests.add')}
        >
          <FormProvider {...methods}>
            <LeaveRequestForm employeeId={employeeId} />
          </FormProvider>
        </ConfirmationDialog>
      )}

      {isRemoveDialogOpen && (
        <ConfirmationDialog
          title={t('EmployeeLeaveRequests.remove')}
          type="warning"
          handleAction={handleRemove}
          cancelBtnText={t('cancel')}
          confirmBtnText={t('delete')}
        >
          <p>{t('EmployeeLeaveRequests.confirm')}</p>
        </ConfirmationDialog>
      )}
    </>
  );
};

export default LeaveRequests;
